import React, { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
    BarChart3,
    ChevronLeft,
    ChevronRight,
    ChevronDown,
    CreditCard,
    FileText,
    KeyRound,
    Loader2,
    LogIn,
    LogOut,
    Mail,
    MessageSquare,
    MousePointerClick,
    Search,
    ShieldCheck,
    Timer,
    UserPlus,
} from 'lucide-react';

import { adminDataApi, AdminAuditLogEntry } from '../../api/adminDataApi';
import SearchableBusinessSelect from '../../components/SearchableBusinessSelect';

type AuditTab = 'login' | 'actions' | 'connections';
type StatusFilter = 'all' | 'success' | 'failed';

const tabs: Array<{ id: AuditTab; label: string }> = [
    { id: 'login', label: 'Login and Security' },
    { id: 'actions', label: 'User Actions' },
    { id: 'connections', label: 'Connections' },
];

const STATIC_ACTION_FILTERS = [
    { label: 'Event: All', value: '' },
    { label: 'API Actions', value: 'api_action' },
    { label: 'Chat', value: 'chat_interaction' },
    { label: 'Billing', value: 'billing' },
    { label: 'Documents', value: 'file_action' },
    { label: 'Transactions', value: 'transaction_update' },
    { label: 'Vendors', value: 'vendor_update' },
    { label: 'Page Views', value: 'page_view' },
    { label: 'Metadata', value: 'metadata_update' },
    { label: 'Scheduler', value: 'scheduler' },
];

const LOGIN_SECURITY_FILTERS = [
    { label: 'Event: All', value: '' },
    { label: 'Login', value: 'login' },
    { label: 'Sign up', value: 'signup' },
    { label: 'Password reset', value: 'password_reset' },
    { label: 'Session', value: 'session' },
];

const CONNECTION_EVENT_PREFIXES = ['stripe', 'qbo', 'bank', 'plaid', 'connection', 'connector'];

const prettifyEvent = (eventType?: string | null) => {
    const explicitLabels: Record<string, string> = {
        login: 'Log in',
        session_refresh: 'Session refreshed',
        signup: 'Sign up',
        signup_updated: 'Sign up updated',
        signup_failed: 'Sign up failed',
        google_login: 'Google sign in',
        google_login_failed: 'Google sign in failed',
        password_reset_requested: 'Password reset requested',
        password_reset_failed: 'Password reset failed',
        password_reset_token_verified: 'Reset token verified',
        password_reset_token_failed: 'Reset token failed',
        password_changed: 'Password changed',
        password_change_failed: 'Password change failed',
    };
    if (eventType && explicitLabels[eventType]) return explicitLabels[eventType];
    const value = String(eventType || 'event')
        .replace(/^user_action:/, '')
        .replace(/^billing:/, 'billing_')
        .replace(/_/g, ' ')
        .replace(/:/g, ' ');
    return value.replace(/\b\w/g, (char) => char.toUpperCase());
};

const getStatus = (entry: AdminAuditLogEntry): 'success' | 'failed' => {
    const raw = JSON.stringify({
        event_type: entry.event_type,
        description: entry.description,
        metadata: entry.metadata,
    }).toLowerCase();
    if (raw.includes('failed') || raw.includes('error') || raw.includes('incorrect') || raw.includes('denied')) {
        return 'failed';
    }
    return 'success';
};

const formatTimeParts = (timestamp?: string | null) => {
    if (!timestamp) return { date: '-', time: '-' };
    const date = new Date(timestamp);
    if (Number.isNaN(date.getTime())) return { date: '-', time: '-' };
    return {
        date: date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }),
        time: date.toLocaleTimeString('en-US', {
            hour: 'numeric',
            minute: '2-digit',
            timeZoneName: 'short',
        }),
    };
};

const getEventIcon = (eventType: string) => {
    const normalized = eventType.toLowerCase();
    if (normalized.includes('signup') || normalized.includes('google_login')) return <UserPlus size={15} />;
    if (normalized.includes('logout')) return <LogOut size={15} />;
    if (normalized.includes('login')) return <LogIn size={15} />;
    if (normalized.includes('password')) return <KeyRound size={15} />;
    if (normalized.includes('email')) return <Mail size={15} />;
    if (normalized.includes('created')) return <UserPlus size={15} />;
    if (normalized.includes('session')) return <Timer size={15} />;
    if (normalized.includes('chat')) return <MessageSquare size={15} />;
    if (normalized.includes('billing')) return <CreditCard size={15} />;
    if (normalized.includes('file') || normalized.includes('document')) return <FileText size={15} />;
    if (normalized.includes('transaction')) return <BarChart3 size={15} />;
    if (normalized.includes('system') || normalized.includes('config')) return <ShieldCheck size={15} />;
    return <MousePointerClick size={15} />;
};

const getUserLabel = (entry: AdminAuditLogEntry, businessMap: Map<string, { name: string; subtitle?: string }>) => {
    const userName = entry.metadata?.user_name || entry.metadata?.user_email || entry.metadata?.email;
    const business = entry.business_id ? businessMap.get(entry.business_id) : undefined;
    return {
        user: String(userName || (entry.user_id?.startsWith('system:') ? entry.user_id : entry.user_id?.slice(0, 8)) || '-'),
        business: business?.name || entry.business_id || entry.workspace_id || '-',
    };
};

const AdminSelect: React.FC<{ value: string; onChange: (value: string) => void; children: React.ReactNode; className?: string }> = ({
    value,
    onChange,
    children,
    className = 'w-[190px]',
}) => (
    <div className={`relative ${className}`}>
        <select
            value={value}
            onChange={(event) => onChange(event.target.value)}
            className="h-11 w-full appearance-none rounded-lg border border-gray-200 bg-white px-4 pr-10 text-sm font-medium text-gray-700 shadow-sm outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500"
        >
            {children}
        </select>
        <ChevronDown className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-500" />
    </div>
);

const AdminAuditTrailPage: React.FC = () => {
    const [activeTab, setActiveTab] = useState<AuditTab>('login');
    const [days, setDays] = useState(7);
    const [eventTypeFilter, setEventTypeFilter] = useState('');
    const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
    const [userIdFilter, setUserIdFilter] = useState('');
    const [businessIdFilter, setBusinessIdFilter] = useState('');
    const [searchDraft, setSearchDraft] = useState('');
    const [search, setSearch] = useState('');
    const [page, setPage] = useState(1);
    const [limit, setLimit] = useState(100);

    const { data: filterOptions } = useQuery({
        queryKey: ['adminAuditFilters', days],
        queryFn: () => adminDataApi.getAuditFilters({ days: 180 }),
    });

    const { data: logsResponse, isLoading, isFetching, refetch } = useQuery({
        queryKey: ['adminAuditLogs', activeTab, days, eventTypeFilter, statusFilter, userIdFilter, businessIdFilter, search, page, limit],
        queryFn: () => {
            const params = {
                days,
                page,
                limit,
                search: search || undefined,
                user_id: userIdFilter || undefined,
                business_id: businessIdFilter || undefined,
            };
            if (activeTab === 'login') {
                return adminDataApi.getLoginAuditLogs({
                    ...params,
                    event_type: eventTypeFilter || undefined,
                    status: statusFilter === 'all' ? undefined : statusFilter,
                });
            }
            if (activeTab === 'connections') return adminDataApi.getActionAuditLogs(params);
            return adminDataApi.getActionAuditLogs({ ...params, event_type: eventTypeFilter || undefined });
        },
    });

    const businessMap = useMemo(() => {
        return new Map((filterOptions?.businesses || []).map((item) => [item.id, { name: item.name, subtitle: item.subtitle }]));
    }, [filterOptions?.businesses]);

    const eventTypeFilters = useMemo(() => {
        const dynamic = (filterOptions?.action_event_types || [])
            .map((option) => ({ label: option.name, value: option.id }))
            .filter((option) => !STATIC_ACTION_FILTERS.some((item) => item.value === option.value));
        return [...STATIC_ACTION_FILTERS, ...dynamic];
    }, [filterOptions]);

    const logs = useMemo(() => {
        const rawLogs = logsResponse?.items || [];
        return rawLogs.filter((entry) => {
            if (activeTab === 'connections') {
                const haystack = `${entry.event_type || ''} ${entry.description || ''}`.toLowerCase();
                if (!CONNECTION_EVENT_PREFIXES.some((prefix) => haystack.includes(prefix))) return false;
            }
            if (statusFilter !== 'all' && getStatus(entry) !== statusFilter) return false;
            return true;
        });
    }, [activeTab, logsResponse?.items, statusFilter]);

    const applySearch = () => {
        setSearch(searchDraft.trim());
        setPage(1);
    };

    const resetFilters = () => {
        setStatusFilter('all');
        setUserIdFilter('');
        setBusinessIdFilter('');
        setEventTypeFilter('');
        setSearch('');
        setSearchDraft('');
        setPage(1);
    };

    const switchTab = (tab: AuditTab) => {
        setActiveTab(tab);
        setEventTypeFilter('');
        setPage(1);
    };

    return (
        <div className="flex h-full flex-col gap-7 bg-gray-50 px-6 py-6">
            <div className="flex items-start justify-between">
                <h1 className="text-2xl font-semibold text-gray-900">Audit</h1>
                <button
                    onClick={() => refetch()}
                    className="h-12 rounded-lg bg-indigo-500 px-8 text-sm font-semibold text-white shadow-sm hover:bg-indigo-600 disabled:opacity-60"
                    disabled={isFetching}
                >
                    Refresh
                </button>
            </div>

            <div className="flex flex-wrap items-center gap-3">
                <div className="relative min-w-[320px] flex-1 max-w-[470px]">
                    <input
                        value={searchDraft}
                        onChange={(event) => setSearchDraft(event.target.value)}
                        onKeyDown={(event) => {
                            if (event.key === 'Enter') applySearch();
                        }}
                        placeholder="Search business name, client name, email,..."
                        className="h-11 w-full rounded-lg border border-gray-200 bg-white pl-4 pr-11 text-sm italic text-gray-700 shadow-sm outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500"
                    />
                    <button
                        onClick={applySearch}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-800"
                        aria-label="Search audit logs"
                    >
                        <Search className="h-5 w-5" />
                    </button>
                </div>

                <AdminSelect value={statusFilter} onChange={(value) => setStatusFilter(value as StatusFilter)}>
                    <option value="all">Status: All</option>
                    <option value="success">Success</option>
                    <option value="failed">Failed</option>
                </AdminSelect>

                <SearchableBusinessSelect
                    options={[{ id: '', name: 'Business: All' }, ...(filterOptions?.businesses || [])]}
                    value={businessIdFilter}
                    onChange={(value) => {
                        setBusinessIdFilter(value);
                        setPage(1);
                    }}
                    placeholder="Business: All"
                    widthClassName="w-[240px]"
                    showSubtitleWhenClosed={false}
                    buttonClassName="relative h-11 w-full rounded-lg border border-gray-200 bg-white py-0 pl-4 pr-10 text-left text-sm font-medium text-gray-700 shadow-sm outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500"
                    menuClassName="absolute top-12 left-0 z-50 w-full overflow-hidden rounded-lg border border-gray-200 bg-white shadow-lg"
                />

                <button onClick={resetFilters} className="h-11 px-4 text-sm font-medium text-indigo-500 hover:text-indigo-700">
                    Reset
                </button>

                <div className="ml-auto flex items-center gap-3">
                    <AdminSelect value={String(days)} onChange={(value) => { setDays(Number(value)); setPage(1); }} className="w-[180px]">
                        <option value="1">Last 24 hours</option>
                        <option value="7">Last 7 days</option>
                        <option value="30">Last 30 days</option>
                        <option value="90">Last 90 days</option>
                    </AdminSelect>
                    <AdminSelect value={String(limit)} onChange={(value) => { setLimit(Number(value)); setPage(1); }} className="w-[160px]">
                        <option value="50">50 rows</option>
                        <option value="100">100 rows</option>
                        <option value="200">200 rows</option>
                    </AdminSelect>
                </div>
            </div>

            <div className="flex items-center gap-8">
                {tabs.map((tab) => (
                    <button
                        key={tab.id}
                        onClick={() => switchTab(tab.id)}
                        className={`border-b-2 pb-3 text-sm font-semibold transition-colors ${
                            activeTab === tab.id
                                ? 'border-indigo-500 text-gray-900'
                                : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-900'
                        }`}
                    >
                        {tab.label}
                    </button>
                ))}
            </div>

            <div>
                <AdminSelect
                    value={eventTypeFilter}
                    onChange={(value) => { setEventTypeFilter(value); setPage(1); }}
                    className="w-[240px]"
                >
                    {(activeTab === 'login' ? LOGIN_SECURITY_FILTERS : activeTab === 'actions' ? eventTypeFilters : [{ label: 'Event: All', value: '' }]).map((item) => (
                        <option key={item.value} value={item.value}>{item.label}</option>
                    ))}
                </AdminSelect>
            </div>

            <div className="min-h-0 flex-1 overflow-hidden rounded-xl border border-gray-200 bg-white">
                {isLoading ? (
                    <div className="flex h-full items-center justify-center">
                        <Loader2 className="h-8 w-8 animate-spin text-indigo-500" />
                    </div>
                ) : (
                    <div className="flex h-full flex-col">
                        <div className="min-h-0 flex-1 overflow-auto">
                            <table className="min-w-full text-left text-sm">
                                <thead className="sticky top-0 z-10 border-b border-gray-200 bg-white text-gray-600">
                                    <tr>
                                        <th className="px-8 py-5 font-medium">Time</th>
                                        <th className="px-8 py-5 font-medium">Event</th>
                                        <th className="px-8 py-5 font-medium">Status</th>
                                        <th className="px-8 py-5 font-medium">User</th>
                                        <th className="px-8 py-5 font-medium">Description</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-100">
                                    {logs.map((entry) => {
                                        const time = formatTimeParts(entry.timestamp);
                                        const status = getStatus(entry);
                                        const user = getUserLabel(entry, businessMap);
                                        return (
                                            <tr key={entry._id || entry.event_id} className="hover:bg-gray-50">
                                                <td className="whitespace-nowrap px-8 py-4 align-top">
                                                    <div className="font-medium text-gray-700">{time.date}</div>
                                                    <div className="mt-1 text-xs text-gray-500">{time.time}</div>
                                                </td>
                                                <td className="px-8 py-4 align-top">
                                                    <div className="flex items-center gap-2 text-gray-600">
                                                        {getEventIcon(entry.event_type)}
                                                        <span className="rounded-full bg-gray-100 px-3 py-1 text-xs font-medium text-gray-700">
                                                            {prettifyEvent(entry.event_type)}
                                                        </span>
                                                    </div>
                                                </td>
                                                <td className="px-8 py-4 align-top">
                                                    <span className={`rounded-md px-3 py-1 text-xs font-medium ${
                                                        status === 'success'
                                                            ? 'bg-emerald-50 text-emerald-700'
                                                            : 'bg-rose-50 text-rose-700'
                                                    }`}>
                                                        {status === 'success' ? 'Success' : 'Failed'}
                                                    </span>
                                                </td>
                                                <td className="px-8 py-4 align-top">
                                                    <div className="font-medium text-gray-700">{user.user}</div>
                                                    <div className="mt-1 max-w-[220px] truncate text-xs text-gray-500">{user.business}</div>
                                                </td>
                                                <td className="px-8 py-4 align-top text-gray-800">{entry.description || prettifyEvent(entry.event_type)}</td>
                                            </tr>
                                        );
                                    })}
                                    {logs.length === 0 && (
                                        <tr>
                                            <td colSpan={5} className="px-8 py-14 text-center text-sm text-gray-500">
                                                No audit events found for this period.
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>

                        <div className="flex items-center justify-between border-t border-gray-200 px-8 py-4 text-sm text-gray-500">
                            <div>
                                {logs.length ? `${(logsResponse?.page || page) === 1 ? 1 : ((page - 1) * limit) + 1}-${((page - 1) * limit) + logs.length} out of ${logsResponse?.total ?? logs.length} results` : '0 results'}
                            </div>
                            <div className="flex items-center gap-4 text-gray-800">
                                <button
                                    onClick={() => setPage((current) => Math.max(1, current - 1))}
                                    disabled={page <= 1 || isFetching}
                                    className="rounded-lg bg-gray-50 p-2 text-gray-400 hover:bg-gray-100 disabled:opacity-50"
                                >
                                    <ChevronLeft className="h-4 w-4" />
                                </button>
                                <span className="font-medium">Page {logsResponse?.page ?? page} of {Math.max(1, Math.ceil((logsResponse?.total || logs.length || 1) / limit))}</span>
                                <button
                                    onClick={() => setPage((current) => current + 1)}
                                    disabled={!logsResponse?.has_next || isFetching}
                                    className="rounded-lg bg-gray-50 p-2 text-gray-400 hover:bg-gray-100 disabled:opacity-50"
                                >
                                    <ChevronRight className="h-4 w-4" />
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default AdminAuditTrailPage;
